import { ReactNode } from 'react';

import AddIcon from '@mui/icons-material/Add';
import TitleIcon from '@mui/icons-material/Title';
import UpdateIcon from '@mui/icons-material/Update';

import Display from 'shared/components/Display';
import { datePrettyDisplay } from 'utils/date';
import jsonObj from 'utils/objects';
import { generateRandomString } from 'utils/string';

import './ViewDetails.scss';

import RenderDefaultValue from './components/RenderDefaultValue';
import RenderStatus from './components/RenderStatus';

export const GENERAL_INFO_TITLE = 'General Information';
export const STATUS_INFO_TITLE = 'Status Information';
export const SETTINGS_TITLE = 'Settings';

export interface ViewDetailsItemT {
  label?: string;
  type?: 'status' | 'creation' | 'update' | 'name';
  dataPath?: string; // JSON key path to get the value
  externalValue?: string; // Set value from outside
  icon?: ReactNode;
  // eslint-disable-next-line no-unused-vars
  renderFn?: (data: any, index?: number) => string;
  hideOnEmpty?: boolean;
  // eslint-disable-next-line no-unused-vars
  linkFn?: (data: any) => string;
  maxWidth?: number; // In rem 1rem -> 10px
}

export interface ViewDetailsSectionT {
  title?: string;
  // eslint-disable-next-line no-unused-vars
  renderSubtitleFn?: (data: any) => string;
  items: ViewDetailsItemT[];
  shape: 1 | 2 | 4; // Nb colums
}

export interface ViewDetailsT {
  sections: ViewDetailsSectionT[];
}

interface ViewDetailsItemProps {
  dataStructure: ViewDetailsItemT;
  data: any;
}
function ViewDetailsItem({ dataStructure, data }: ViewDetailsItemProps) {
  switch (dataStructure.type) {
    case 'status':
      return (
        <RenderStatus
          title={dataStructure.label as string}
          status={jsonObj.getDescendantProp(data, dataStructure.dataPath as string)}
        />
      );
    case 'creation':
      return (
        <Display.CardWithIcon
          icon={<AddIcon color="primary" />}
          name="Creation"
          value={`${datePrettyDisplay(data?.createdAt || data?.creationTimestamp)} by ${data?.createdBy}`}
          width="50rem"
        />
      );
    case 'update':
      return (
        <Display.CardWithIcon
          icon={<UpdateIcon color="primary" />}
          name="Last Update"
          value={datePrettyDisplay(data?.lastUpdated || data?.lastUpdateTimestamp)}
          width="50rem"
        />
      );
    case 'name':
      return (
        <Display.CardWithIcon
          icon={<TitleIcon color="primary" />}
          name="Name"
          value={data?.name as string}
          width="50rem"
        />
      );
    default:
      return <RenderDefaultValue dataStructure={dataStructure} data={data} />;
  }
}

interface ViewDetailsProps {
  dataStructure: ViewDetailsT;
  data: any;
}
function ViewDetails({ dataStructure, data }: ViewDetailsProps) {
  return (
    <>
      {
        dataStructure.sections.map((section) => (
          <div
            key={generateRandomString()}
            className="view-details__section"
          >
            {
              section.title
                ? (
                  // @ts-ignore
                  <h3
                    sub-title={
                      section.renderSubtitleFn
                        ? section.renderSubtitleFn(data)
                        : undefined
                    }
                  >
                    { section.title }
                  </h3>
                )
                : null
            }
            <div className={`view-details__section--grid__col--${section.shape}`}>
              {
                section.items.map((item) => (
                  <ViewDetailsItem
                    key={generateRandomString()}
                    dataStructure={item}
                    data={data}
                  />
                ))
              }
            </div>
          </div>
        ))
      }
    </>
  );
}

export default ViewDetails;
